import { Polygon, Point2D, createPolygon } from './segmentation/types';
import { LinePromptDebugData, LinePromptDebugFlags } from './PolygonProcessor';

/** Subset of prompt point fields used by line prompt edge adjustment */
export interface LinePromptPoint {
  label: number;
  type?: 'point' | 'box' | 'vertex' | 'line';
  line?: {
    start: { x: number; y: number };
    end: { x: number; y: number };
  };
}

/** Dependencies injected from PolygonProcessor */
export interface LinePromptAdjusterContext {
  promptPoints: ReadonlyArray<LinePromptPoint>;
  pixelsToCm: (px: number) => number;
  cmToPixels: (cm: number) => number;
  rayIntersectsEdge: (origin: Point2D, direction: Point2D, edgeStart: Point2D, edgeEnd: Point2D) => { distance: number; point: Point2D } | null;
  infiniteLineIntersection: (pointA: Point2D, dirA: Point2D, pointB: Point2D, dirB: Point2D) => Point2D | null;
}

/**
 * Adjust polygon edges based on line prompts (batch processor).
 * For each line prompt, casts perpendicular rays to find the closest contiguous
 * edge chain, then moves that chain to align with the prompt line.
 * Chain endpoint vertices slide along their neighbor edges to maintain connectivity.
 * @param ctx Dependencies from PolygonProcessor
 * @param polygons Polygons in world space (cm)
 * @param raySampleCount Number of sample points along the prompt line for ray casting
 * @param maxRayDistanceCm Maximum perpendicular distance to consider for edge hits
 * @param mergeThresholdCm Merge threshold (unused in current implementation but kept for API compat)
 * @param pullModeRatio If the prompt projection is shorter than this ratio of the chain span, use pull-out mode instead of snapping the entire chain (0-1)
 * @param debugFlags Optional per-step debug toggle flags
 */
export function adjustEdgesForLinePrompts(
  ctx: LinePromptAdjusterContext,
  polygons: Polygon[],
  raySampleCount = 20,
  maxRayDistanceCm = 50,
  _mergeThresholdCm = 3,
  pullModeRatio = 0.7,
  debugFlags?: LinePromptDebugFlags,
): { polygons: Polygon[]; linePromptDebug: LinePromptDebugData | null } {
  const dbg = debugFlags;
  const anyDebug = dbg && Object.values(dbg).some((v) => v);

  let linePromptDebug: LinePromptDebugData | null;

  if (anyDebug) {
    linePromptDebug = {
      rays: [],
      rayHits: [],
      candidateEdges: [],
      allChains: [],
      selectedChainOriginal: [],
      collapsedEdge: null,
      seedEdge: null,
      startOuterEdges: [],
      endOuterEdges: [],
    };
  } else {
    linePromptDebug = null;
  }

  if (ctx.promptPoints.length === 0 || polygons.length === 0) {
    return { polygons, linePromptDebug };
  }

  // Filter line prompts, excluding label=2 (remove_points)
  const linePrompts = ctx.promptPoints.filter((p) => p.type === 'line' && p.line && p.label !== 2);
  if (linePrompts.length === 0) {
    return { polygons, linePromptDebug };
  }

  // Clone all polygons with their children
  const adjustedPolygons: Polygon[] = polygons.map((p) => {
    const cloned = createPolygon(p.points.slice(), p.isHole);
    if (p.children && p.children.length > 0) {
      cloned.children = p.children.map((child) => createPolygon(child.points.slice(), child.isHole));
    }
    return cloned;
  });

  // Flatten polygons and holes into a single list for processing
  const flatPolygons: { polygon: Polygon; parentIndex: number; isChild: boolean; childIndex: number }[] = [];
  for (let i = 0; i < adjustedPolygons.length; i++) {
    flatPolygons.push({ polygon: adjustedPolygons[i], parentIndex: i, isChild: false, childIndex: -1 });
    const children = adjustedPolygons[i].children;
    if (children) {
      for (let j = 0; j < children.length; j++) {
        flatPolygons.push({
          polygon: children[j],
          parentIndex: i,
          isChild: true,
          childIndex: j,
        });
      }
    }
  }

  // Process each line prompt
  for (const prompt of linePrompts) {
    if (!prompt.line) continue;

    // Convert prompt line endpoints to cm space
    const startCm: Point2D = {
      x: ctx.pixelsToCm(prompt.line.start.x),
      y: ctx.pixelsToCm(prompt.line.start.y),
    };
    const endCm: Point2D = {
      x: ctx.pixelsToCm(prompt.line.end.x),
      y: ctx.pixelsToCm(prompt.line.end.y),
    };

    const dx = endCm.x - startCm.x;
    const dy = endCm.y - startCm.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length < 1e-6) continue; // Degenerate line, skip

    const direction: Point2D = { x: dx / length, y: dy / length };
    const normal: Point2D = { x: -direction.y, y: direction.x };

    // Debug helper: convert cm point to pixel space
    const cmToPx = (p: Point2D): Point2D => ({
      x: ctx.cmToPixels(p.x),
      y: ctx.cmToPixels(p.y),
    });

    // Cast perpendicular rays from sample points along the prompt line.
    // Track which side (sign) each hit came from so we can separate
    // near-side vs far-side edges within the same polygon.
    const hits: Array<{
      flatIndex: number;
      edgeIndex: number;
      hitPoint: Point2D;
      distance: number;
      sampleT: number;
      side: number; // +1 or -1
    }> = [];

    for (let s = 0; s <= raySampleCount; s++) {
      const t = s / raySampleCount;
      const samplePoint: Point2D = {
        x: startCm.x + t * dx,
        y: startCm.y + t * dy,
      };

      // Cast ray in both +normal and -normal directions.
      // Only keep the closest hit per direction.
      for (const sign of [1, -1]) {
        const rayDir: Point2D = { x: normal.x * sign, y: normal.y * sign };

        let closestHit: { flatIndex: number; edgeIndex: number; hitPoint: Point2D; distance: number } | null = null;

        for (let flatIdx = 0; flatIdx < flatPolygons.length; flatIdx++) {
          const poly = flatPolygons[flatIdx].polygon;
          for (let edgeIdx = 0; edgeIdx < poly.length; edgeIdx++) {
            const edgeStart = poly.points[edgeIdx];
            const edgeEnd = poly.points[(edgeIdx + 1) % poly.length];

            const hit = ctx.rayIntersectsEdge(samplePoint, rayDir, edgeStart, edgeEnd);
            if (hit && hit.distance <= maxRayDistanceCm) {
              if (!closestHit || hit.distance < closestHit.distance) {
                closestHit = {
                  flatIndex: flatIdx,
                  edgeIndex: edgeIdx,
                  hitPoint: hit.point,
                  distance: hit.distance,
                };
              }
            }
          }
        }

        if (closestHit) {
          hits.push({
            ...closestHit,
            sampleT: t,
            side: sign,
          });
          if (dbg?.debugRays && linePromptDebug) {
            linePromptDebug.rays.push({
              start: cmToPx(samplePoint),
              end: cmToPx(closestHit.hitPoint),
            });
            linePromptDebug.rayHits.push(cmToPx(closestHit.hitPoint));
          }
        }
      }
    }

    if (hits.length === 0) continue;

    // Group hits by edge identity.
    const edgeGroups = new Map<string, typeof hits>();
    for (const hit of hits) {
      const key = `${hit.flatIndex}:${hit.edgeIndex}`;
      let group = edgeGroups.get(key);
      if (!group) {
        group = [];
        edgeGroups.set(key, group);
      }
      group.push(hit);
    }

    // Build all hit edges and analyze crossing positions along the prompt line
    const hitEdges: Array<{
      flatIndex: number;
      edgeIndex: number;
      minDist: number;
      hitCount: number;
      avgSampleT: number; // Average position along prompt line (0=start, 1=end)
    }> = [];

    for (const [key, group] of edgeGroups) {
      const parts = key.split(':');
      const flatIdx = parseInt(parts[0]);
      const edgeIdx = parseInt(parts[1]);
      const avgSampleT = group.reduce((sum, h) => sum + h.sampleT, 0) / group.length;

      hitEdges.push({
        flatIndex: flatIdx,
        edgeIndex: edgeIdx,
        minDist: Math.min(...group.map((h) => h.distance)),
        hitCount: group.length,
        avgSampleT: avgSampleT,
      });
    }

    if (hitEdges.length === 0) continue;

    // Debug: record candidate edges
    if (dbg?.debugCandidateEdges && linePromptDebug) {
      for (const ce of hitEdges) {
        const poly = flatPolygons[ce.flatIndex].polygon;
        const eStart = poly.points[ce.edgeIndex];
        const eEnd = poly.points[(ce.edgeIndex + 1) % poly.length];
        linePromptDebug.candidateEdges.push({
          start: cmToPx(eStart),
          end: cmToPx(eEnd),
        });
      }
    }

    interface EdgeChain {
      flatIndex: number;
      edgeIndices: number[];
      avgDistance: number;
    }

    // Seed = edge with shortest ray-hit distance. Always in the chain.
    const seed = hitEdges.reduce((best, e) => (e.minDist < best.minDist ? e : best));
    const seedFlatIdx = seed.flatIndex;
    const seedEdgeIdx = seed.edgeIndex;

    const seedPoly = flatPolygons[seedFlatIdx].polygon;
    const polyLen = seedPoly.length;

    // Find ALL edges of the seed polygon that intersect with the prompt line
    // This includes edges that weren't hit by perpendicular rays (e.g., parallel edges)
    const crossingEdgesWithIntersection: Array<{
      edgeIndex: number;
      intersectionT: number; // Parameter along prompt line (0=start, 1=end)
      intersectionPoint: Point2D;
    }> = [];

    for (let eIdx = 0; eIdx < polyLen; eIdx++) {
      const edgeStart = seedPoly.points[eIdx];
      const edgeEnd = seedPoly.points[(eIdx + 1) % polyLen];

      // Calculate edge direction
      const edgeDx = edgeEnd.x - edgeStart.x;
      const edgeDy = edgeEnd.y - edgeStart.y;
      const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);

      if (edgeLen < 1e-6) continue;

      // Find intersection between edge line and prompt line (infinite lines)
      const intersection = ctx.infiniteLineIntersection(startCm, direction, edgeStart, { x: edgeDx, y: edgeDy });

      if (intersection) {
        // Check if intersection is actually on the edge segment
        const toIntersection = { x: intersection.x - edgeStart.x, y: intersection.y - edgeStart.y };
        const edgeT = (toIntersection.x * edgeDx + toIntersection.y * edgeDy) / (edgeLen * edgeLen);

        // Only consider if intersection is on the edge segment (with small tolerance)
        if (edgeT >= -0.01 && edgeT <= 1.01) {
          // Calculate parameter t along prompt line (0 = startCm, 1 = endCm)
          const toIntersectionFromPromptStart = { x: intersection.x - startCm.x, y: intersection.y - startCm.y };
          const projectionOnPrompt = toIntersectionFromPromptStart.x * direction.x + toIntersectionFromPromptStart.y * direction.y;
          const intersectionT = projectionOnPrompt / length;

          // Also check if intersection is on the prompt line segment (with tolerance)
          // We allow some extension beyond the prompt line endpoints for the 25% zones
          if (intersectionT >= -0.01 && intersectionT <= 1.01) {
            crossingEdgesWithIntersection.push({
              edgeIndex: eIdx,
              intersectionT: intersectionT,
              intersectionPoint: intersection,
            });
          }
        }
      }
    }

    // Find crossing edges closest to the prompt line endpoints
    // These will be used as outer edges (chain extent) and for trimming

    // For start: find the crossing edge on the start side (intersectionT < 0.5) closest to startCm
    let lastCrossingStartEdge: number | null = null;
    let startTrimPoint: Point2D | null = null;

    const onStartSide = crossingEdgesWithIntersection.filter((e) => e.intersectionT < 0.5);
    if (onStartSide.length > 0) {
      // Sort by actual distance to startCm - pick the closest
      onStartSide.sort((a, b) => {
        const distA = Math.sqrt((a.intersectionPoint.x - startCm.x) ** 2 + (a.intersectionPoint.y - startCm.y) ** 2);
        const distB = Math.sqrt((b.intersectionPoint.x - startCm.x) ** 2 + (b.intersectionPoint.y - startCm.y) ** 2);
        return distA - distB;
      });
      lastCrossingStartEdge = onStartSide[0].edgeIndex;
      startTrimPoint = onStartSide[0].intersectionPoint;
    }

    // For end: find the crossing edge on the end side (intersectionT > 0.5) closest to endCm
    let lastCrossingEndEdge: number | null = null;
    let endTrimPoint: Point2D | null = null;

    const onEndSide = crossingEdgesWithIntersection.filter((e) => e.intersectionT > 0.5);
    if (onEndSide.length > 0) {
      // Sort by actual distance to endCm - pick the closest
      onEndSide.sort((a, b) => {
        const distA = Math.sqrt((a.intersectionPoint.x - endCm.x) ** 2 + (a.intersectionPoint.y - endCm.y) ** 2);
        const distB = Math.sqrt((b.intersectionPoint.x - endCm.x) ** 2 + (b.intersectionPoint.y - endCm.y) ** 2);
        return distA - distB;
      });
      lastCrossingEndEdge = onEndSide[0].edgeIndex;
      endTrimPoint = onEndSide[0].intersectionPoint;
    }

    // Debug: record seed edge
    if (dbg?.debugSeed && linePromptDebug) {
      const eStart = seedPoly.points[seedEdgeIdx];
      const eEnd = seedPoly.points[(seedEdgeIdx + 1) % polyLen];
      linePromptDebug.seedEdge = { start: cmToPx(eStart), end: cmToPx(eEnd) };
    }

    // Debug: record seed edge
    if (dbg?.debugSeed && linePromptDebug) {
      const eStart = seedPoly.points[seedEdgeIdx];
      const eEnd = seedPoly.points[(seedEdgeIdx + 1) % polyLen];
      linePromptDebug.seedEdge = { start: cmToPx(eStart), end: cmToPx(eEnd) };
    }

    // Collect ALL candidates for start and end outer edges
    // Three types of candidates:
    // 1. Crossing edges - intersections with prompt line (have trim points)
    // 2. Perpendicular raycasts - perpendicular rays (NO trim points)
    // 3. Endpoint raycasts - rays along prompt direction from endpoints (have trim points)

    interface OuterEdgeCandidate {
      edgeIndex: number;
      distance: number; // Distance from prompt endpoint to the edge
      trimPoint: Point2D | null; // Point where chain should be trimmed (null for perpendicular rays)
      parallelism: number; // abs(dot(edgeDir, promptDir)), 0 = perpendicular, 1 = parallel
    }

    // Compute parallelism between an edge and the prompt direction
    const edgeParallelism = (eIdx: number): number => {
      const eStart = seedPoly.points[eIdx];
      const eEnd = seedPoly.points[(eIdx + 1) % polyLen];
      const eDx = eEnd.x - eStart.x;
      const eDy = eEnd.y - eStart.y;
      const eLen = Math.sqrt(eDx * eDx + eDy * eDy);
      if (eLen < 1e-8) return 0;
      return Math.abs((eDx / eLen) * direction.x + (eDy / eLen) * direction.y);
    };

    // Score a candidate: lower is better.
    // Parallelism factor uses pow(0.3) so slight parallelism helps a lot,
    // but near-perfect parallelism gives diminishing returns.
    // A small epsilon prevents division by zero for fully perpendicular edges.
    const scoreCandidate = (c: OuterEdgeCandidate): number => {
      const parallelismFactor = Math.pow(c.parallelism + 0.01, 0.3);
      return c.distance / parallelismFactor;
    };

    const startCandidates: OuterEdgeCandidate[] = [];
    const endCandidates: OuterEdgeCandidate[] = [];

    // Type 1: Add crossing edge candidates (have trim points)
    // Use parallelism: 1 so crossing edges are scored purely by distance,
    // not penalized by the parallelism factor in scoreCandidate.
    if (lastCrossingStartEdge !== null && startTrimPoint !== null) {
      const dx = startTrimPoint.x - startCm.x;
      const dy = startTrimPoint.y - startCm.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      startCandidates.push({
        edgeIndex: lastCrossingStartEdge,
        distance,
        trimPoint: startTrimPoint,
        parallelism: 1,
      });
    }

    if (lastCrossingEndEdge !== null && endTrimPoint !== null) {
      const dx = endTrimPoint.x - endCm.x;
      const dy = endTrimPoint.y - endCm.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      endCandidates.push({
        edgeIndex: lastCrossingEndEdge,
        distance,
        trimPoint: endTrimPoint,
        parallelism: 1,
      });
    }

    // Type 2: Add all ray-hit edges from the seed polygon as candidates (NO trim points)
    // Use avgSampleT to compute distance from each prompt endpoint:
    // avgSampleT near 0 = close to start, near 1 = close to end.
    // Combine perpendicular distance (minDist) with along-line distance for the score.
    const seedHitEdges = hitEdges.filter((e) => e.flatIndex === seedFlatIdx);
    for (const he of seedHitEdges) {
      const par = edgeParallelism(he.edgeIndex);
      const alongLineDistFromStart = he.avgSampleT * length;
      const alongLineDistFromEnd = (1 - he.avgSampleT) * length;
      startCandidates.push({
        edgeIndex: he.edgeIndex,
        distance: Math.sqrt(he.minDist * he.minDist + alongLineDistFromStart * alongLineDistFromStart),
        trimPoint: null,
        parallelism: par,
      });
      endCandidates.push({
        edgeIndex: he.edgeIndex,
        distance: Math.sqrt(he.minDist * he.minDist + alongLineDistFromEnd * alongLineDistFromEnd),
        trimPoint: null,
        parallelism: par,
      });
    }

    // Type 3: Add endpoint raycast candidates along prompt direction (have trim points)
    // Only cast outward from each endpoint — away from the prompt line, not into it.
    const backwardDir: Point2D = { x: -direction.x, y: -direction.y };
    const forwardDir: Point2D = { x: direction.x, y: direction.y };

    // Cast from startCm backward (away from endCm) -> start outer edge
    let closestFromStartBackward: { edgeIndex: number; distance: number; hitPoint: Point2D } | null = null;
    for (let eIdx = 0; eIdx < polyLen; eIdx++) {
      const eStart = seedPoly.points[eIdx];
      const eEnd = seedPoly.points[(eIdx + 1) % polyLen];
      const hitBackward = ctx.rayIntersectsEdge(startCm, backwardDir, eStart, eEnd);
      if (hitBackward && hitBackward.distance <= maxRayDistanceCm && (!closestFromStartBackward || hitBackward.distance < closestFromStartBackward.distance)) {
        closestFromStartBackward = { edgeIndex: eIdx, distance: hitBackward.distance, hitPoint: hitBackward.point };
      }
    }
    if (closestFromStartBackward) {
      const distToStart = Math.sqrt((closestFromStartBackward.hitPoint.x - startCm.x) ** 2 + (closestFromStartBackward.hitPoint.y - startCm.y) ** 2);
      startCandidates.push({
        edgeIndex: closestFromStartBackward.edgeIndex,
        distance: distToStart,
        trimPoint: closestFromStartBackward.hitPoint,
        parallelism: 1,
      });
    }

    // Cast from endCm forward (away from startCm) -> end outer edge
    let closestFromEndForward: { edgeIndex: number; distance: number; hitPoint: Point2D } | null = null;
    for (let eIdx = 0; eIdx < polyLen; eIdx++) {
      const eStart = seedPoly.points[eIdx];
      const eEnd = seedPoly.points[(eIdx + 1) % polyLen];
      const hitForward = ctx.rayIntersectsEdge(endCm, forwardDir, eStart, eEnd);
      if (hitForward && hitForward.distance <= maxRayDistanceCm && (!closestFromEndForward || hitForward.distance < closestFromEndForward.distance)) {
        closestFromEndForward = { edgeIndex: eIdx, distance: hitForward.distance, hitPoint: hitForward.point };
      }
    }
    if (closestFromEndForward) {
      const distToEnd = Math.sqrt((closestFromEndForward.hitPoint.x - endCm.x) ** 2 + (closestFromEndForward.hitPoint.y - endCm.y) ** 2);
      endCandidates.push({
        edgeIndex: closestFromEndForward.edgeIndex,
        distance: distToEnd,
        trimPoint: closestFromEndForward.hitPoint,
        parallelism: 1,
      });
    }

    // Pick the best candidate using weighted score (distance / parallelism factor)
    const bestStartCandidate = startCandidates.reduce((best, candidate) => (!best || scoreCandidate(candidate) < scoreCandidate(best) ? candidate : best), null as OuterEdgeCandidate | null);

    const bestEndCandidate = endCandidates.reduce((best, candidate) => (!best || scoreCandidate(candidate) < scoreCandidate(best) ? candidate : best), null as OuterEdgeCandidate | null);

    // Update the outer edge indices and trim points based on best candidates
    const startOuterEdgeIndices = new Set<number>();
    const endOuterEdgeIndices = new Set<number>();

    if (bestStartCandidate) {
      startOuterEdgeIndices.add(bestStartCandidate.edgeIndex);
      if (bestStartCandidate.trimPoint) {
        lastCrossingStartEdge = bestStartCandidate.edgeIndex;
        startTrimPoint = bestStartCandidate.trimPoint;
      } else {
        // Best candidate has no trim point (perpendicular ray), but another candidate
        // for the same edge might have one (crossing or endpoint raycast). Use it if found.
        const samEdgeTrimCandidate = startCandidates.find((c) => c.edgeIndex === bestStartCandidate.edgeIndex && c.trimPoint !== null && Math.sqrt((c.trimPoint.x - startCm.x) ** 2 + (c.trimPoint.y - startCm.y) ** 2) <= 15);
        if (samEdgeTrimCandidate) {
          lastCrossingStartEdge = samEdgeTrimCandidate.edgeIndex;
          startTrimPoint = samEdgeTrimCandidate.trimPoint;
        } else {
          lastCrossingStartEdge = null;
          startTrimPoint = null;
        }
      }
    }

    if (bestEndCandidate) {
      endOuterEdgeIndices.add(bestEndCandidate.edgeIndex);
      if (bestEndCandidate.trimPoint) {
        lastCrossingEndEdge = bestEndCandidate.edgeIndex;
        endTrimPoint = bestEndCandidate.trimPoint;
      } else {
        // Best candidate has no trim point (perpendicular ray), but another candidate
        // for the same edge might have one (crossing or endpoint raycast). Use it if found.
        const sameEdgeTrimCandidate = endCandidates.find((c) => c.edgeIndex === bestEndCandidate.edgeIndex && c.trimPoint !== null && Math.sqrt((c.trimPoint.x - endCm.x) ** 2 + (c.trimPoint.y - endCm.y) ** 2) <= 15);
        if (sameEdgeTrimCandidate) {
          lastCrossingEndEdge = sameEdgeTrimCandidate.edgeIndex;
          endTrimPoint = sameEdgeTrimCandidate.trimPoint;
        } else {
          lastCrossingEndEdge = null;
          endTrimPoint = null;
        }
      }
    }

    // Debug visualization will be done after we know if trimming will occur

    // All hit edge indices for the seed polygon.
    // Include edges from perpendicular scan rays AND outer edge candidates (raycasts + fallback)
    const hitEdgeSet = new Set(hitEdges.filter((e) => e.flatIndex === seedFlatIdx).map((e) => e.edgeIndex));

    // Add outer edge candidates to hitEdgeSet so growFromEdge recognizes them
    // This ensures intermediate filler edges are properly included when building the chain
    for (const edgeIdx of startOuterEdgeIndices) {
      hitEdgeSet.add(edgeIdx);
    }
    for (const edgeIdx of endOuterEdgeIndices) {
      hitEdgeSet.add(edgeIdx);
    }

    // Map of edges that will be split → their trim points
    const edgeSplitPoints = new Map<number, Point2D>();
    if (lastCrossingStartEdge !== null && startTrimPoint !== null) {
      edgeSplitPoints.set(lastCrossingStartEdge, startTrimPoint);
    }
    if (lastCrossingEndEdge !== null && endTrimPoint !== null) {
      edgeSplitPoints.set(lastCrossingEndEdge, endTrimPoint);
    }

    // Returns distances from trim point to each end of the edge
    const edgeSplitLengths = (edgeIdx: number, splitPt: Point2D): { firstHalf: number; secondHalf: number } => {
      const p1 = seedPoly.points[edgeIdx];
      const p2 = seedPoly.points[(edgeIdx + 1) % polyLen];
      return {
        firstHalf: Math.sqrt((splitPt.x - p1.x) ** 2 + (splitPt.y - p1.y) ** 2),
        secondHalf: Math.sqrt((p2.x - splitPt.x) ** 2 + (p2.y - splitPt.y) ** 2),
      };
    };

    // Calculate actual distance between two edges in a given direction.
    // For edges with trim points, uses the partial length (trim point to edge end)
    // instead of the full edge length, simulating the post-split chain distance.
    const distanceBetween = (fromEdge: number, toEdge: number, step: 1 | -1): number => {
      if (fromEdge === toEdge) return 0;

      let totalDistance = 0;
      let currentEdge = fromEdge;

      for (let s = 0; s < polyLen; s++) {
        const nextEdge = (currentEdge + step + polyLen) % polyLen;

        // Use partial length if this edge has a split point
        const splitPt = edgeSplitPoints.get(currentEdge);
        if (splitPt) {
          const splits = edgeSplitLengths(currentEdge, splitPt);
          // Forward: chain uses trimPoint→edgeEnd (secondHalf)
          // Backward: chain uses edgeStart→trimPoint (firstHalf)
          totalDistance += step === 1 ? splits.secondHalf : splits.firstHalf;
        } else {
          const p1 = seedPoly.points[currentEdge];
          const p2 = seedPoly.points[(currentEdge + 1) % polyLen];
          const edgeDx = p2.x - p1.x;
          const edgeDy = p2.y - p1.y;
          totalDistance += Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
        }

        if (nextEdge === toEdge) {
          return totalDistance;
        }

        currentEdge = nextEdge;
      }

      return Infinity; // Shouldn't reach here
    };

    // Count the number of edges between two edges in a given direction
    const edgeCountBetween = (fromEdge: number, toEdge: number, step: 1 | -1): number => {
      if (fromEdge === toEdge) return 0;

      let count = 0;
      let currentEdge = fromEdge;

      for (let s = 0; s < polyLen; s++) {
        currentEdge = (currentEdge + step + polyLen) % polyLen;
        count++;

        if (currentEdge === toEdge) {
          return count;
        }
      }

      return polyLen; // Full loop
    };

    // Step 1: Find the single closest outer edge to the seed (could be start or end)
    let closestToSeed: { edge: number; isStart: boolean; distFwd: number; distBwd: number; minDist: number } | null = null;

    // Check all start edges
    for (const startEdge of startOuterEdgeIndices) {
      const distFwd = distanceBetween(seedEdgeIdx, startEdge, 1);
      const distBwd = distanceBetween(seedEdgeIdx, startEdge, -1);
      const minDist = Math.min(distFwd, distBwd);

      if (!closestToSeed || minDist < closestToSeed.minDist) {
        closestToSeed = { edge: startEdge, isStart: true, distFwd, distBwd, minDist };
      }
    }

    // Check all end edges
    for (const endEdge of endOuterEdgeIndices) {
      const distFwd = distanceBetween(seedEdgeIdx, endEdge, 1);
      const distBwd = distanceBetween(seedEdgeIdx, endEdge, -1);
      const minDist = Math.min(distFwd, distBwd);

      if (!closestToSeed || minDist < closestToSeed.minDist) {
        closestToSeed = { edge: endEdge, isStart: false, distFwd, distBwd, minDist };
      }
    }

    let targetStartEdge: number;
    let targetEndEdge: number;
    let toStartDirection: 1 | -1;
    let toStartMaxSteps: number;

    if (!closestToSeed) {
      // Fallback - no outer edges found
      targetStartEdge = seedEdgeIdx;
      targetEndEdge = seedEdgeIdx;
      toStartDirection = 1;
      toStartMaxSteps = 0;
    } else {
      // Step 2: From the closest edge, find the closest counterpart of the opposite type
      const needToFindStart = !closestToSeed.isStart; // If closest is end, we need to find start
      const counterpartSet = needToFindStart ? startOuterEdgeIndices : endOuterEdgeIndices;

      let closestCounterpart: { edge: number; distFwd: number; distBwd: number } | null = null;

      for (const counterpartEdge of counterpartSet) {
        const distFwd = distanceBetween(closestToSeed.edge, counterpartEdge, 1);
        const distBwd = distanceBetween(closestToSeed.edge, counterpartEdge, -1);
        const minDist = Math.min(distFwd, distBwd);

        if (!closestCounterpart || minDist < Math.min(closestCounterpart.distFwd, closestCounterpart.distBwd)) {
          closestCounterpart = { edge: counterpartEdge, distFwd, distBwd };
        }
      }

      if (!closestCounterpart) {
        // Fallback - no counterpart found
        targetStartEdge = seedEdgeIdx;
        targetEndEdge = seedEdgeIdx;
        toStartDirection = 1;
        toStartMaxSteps = 0;
      } else {
        // Build a direct path from the closest edge to its counterpart
        // Assign start and end based on which was found first
        if (closestToSeed.isStart) {
          targetStartEdge = closestToSeed.edge;
          targetEndEdge = closestCounterpart.edge;
        } else {
          targetStartEdge = closestCounterpart.edge;
          targetEndEdge = closestToSeed.edge;
        }

        // Determine shortest direction from targetStartEdge to targetEndEdge
        const fwdDist = distanceBetween(targetStartEdge, targetEndEdge, 1);
        const bwdDist = distanceBetween(targetStartEdge, targetEndEdge, -1);

        // If start and end are the same edge, don't traverse at all
        if (targetStartEdge === targetEndEdge) {
          toStartDirection = 1;
          toStartMaxSteps = 0;
        } else if (fwdDist <= bwdDist) {
          toStartDirection = 1;
          toStartMaxSteps = edgeCountBetween(targetStartEdge, targetEndEdge, 1);
        } else {
          toStartDirection = -1;
          toStartMaxSteps = edgeCountBetween(targetStartEdge, targetEndEdge, -1);
        }
      }
    }

    const growFromEdge = (fromEdge: number, step: 1 | -1, maxSteps: number, targetEdge: number): number[] => {
      const path: number[] = [];
      const edgesToInclude: number[] = [];

      for (let s = 1; s <= maxSteps; s++) {
        const idx = (fromEdge + s * step + polyLen) % polyLen;

        // Always add the current edge to our candidate list
        edgesToInclude.push(idx);

        // If we found the specific target edge, include all edges up to and including it, then stop
        if (idx === targetEdge) {
          path.push(...edgesToInclude);
          break;
        }

        // If we found a hit edge, include all edges up to and including it
        if (hitEdgeSet.has(idx)) {
          path.push(...edgesToInclude);
          edgesToInclude.length = 0; // Clear the candidate list
        }
        // If it's not a hit edge and not a target, it's a filler edge
        // Continue accumulating filler edges - only commit them if we find a hit or target ahead
      }

      return path;
    };

    // Build the chain - direct path from start edge to end edge
    // Grow from start edge to end edge
    const pathEdges = growFromEdge(targetStartEdge, toStartDirection, toStartMaxSteps, targetEndEdge);
    const edgeIndices = [targetStartEdge, ...pathEdges];

    const bestChain: EdgeChain = { flatIndex: seedFlatIdx, edgeIndices, avgDistance: seed.minDist };

    const poly = flatPolygons[bestChain.flatIndex].polygon;
    let chainEdgeIndices = bestChain.edgeIndices;

    // --- Normalize backward chains ---
    // If the chain was built backward (toStartDirection = -1), reverse it to make it forward
    // This ensures consistent processing regardless of prompt direction
    if (chainEdgeIndices.length > 1 && toStartDirection === -1) {
      chainEdgeIndices.reverse();
    }

    // Check if crossing edges are actually in the chain before deciding to trim
    const startCrossingInChain = lastCrossingStartEdge !== null && chainEdgeIndices.indexOf(lastCrossingStartEdge) !== -1;
    const endCrossingInChain = lastCrossingEndEdge !== null && chainEdgeIndices.indexOf(lastCrossingEndEdge) !== -1;

    // Trim the chain edges at intersection points if we found crossing edges
    // This will be applied after we build the chain vertices
    // Skip trimming for single-edge chains — the collapse/projection step will snap it
    // to the prompt line, and splitting a single edge can produce incorrect fragments.
    const singleEdgeChain = chainEdgeIndices.length === 1;
    const trimAtStart = startTrimPoint !== null && startCrossingInChain && !singleEdgeChain;
    const trimAtEnd = endTrimPoint !== null && endCrossingInChain && !singleEdgeChain;

    // Debug: record start and end outer edges (before trimming/rotation)
    // Edges that will be trimmed are visualized after trimming
    if (linePromptDebug) {
      // Only visualize edges if NOT trimming (trimmed edges are visualized later)
      if (dbg?.debugStartOuterEdges && !trimAtStart) {
        for (const oIdx of startOuterEdgeIndices) {
          const eStart = seedPoly.points[oIdx];
          const eEnd = seedPoly.points[(oIdx + 1) % polyLen];
          linePromptDebug.startOuterEdges.push({ start: cmToPx(eStart), end: cmToPx(eEnd) });
        }
      }
      if (dbg?.debugEndOuterEdges && !trimAtEnd) {
        for (const oIdx of endOuterEdgeIndices) {
          const eStart = seedPoly.points[oIdx];
          const eEnd = seedPoly.points[(oIdx + 1) % polyLen];
          linePromptDebug.endOuterEdges.push({ start: cmToPx(eStart), end: cmToPx(eEnd) });
        }
      }
    }

    const isFullLoop = chainEdgeIndices.length >= poly.length;
    // --- Normalize wrap-around chains ---
    // If the chain wraps past the polygon origin (e.g. edges [5,6,7,0]),
    // rotate the polygon's points so the chain becomes contiguous and
    // doesn't cross index 0. This avoids complex wrap-around splice logic.
    {
      // Normalize wrap-around: if the chain wraps past the polygon origin,
      // rotate the polygon so the chain is contiguous.
      // IMPORTANT: We normalize ALL wrapping chains, even full loops, because
      // a full loop chain might NOT be treated as actualFullLoop later (if prompt is small),
      // and will then need normalized indices for standard mode collapse.
      const isWrapping = chainEdgeIndices.length > 1 && chainEdgeIndices.some((eIdx, i) => i > 0 && eIdx < chainEdgeIndices[i - 1]);

      if (isWrapping) {
        // Find where the wrap point is — the first edge in the chain that's before
        // the chain's start vertex. Everything from wrap onwards are the "low" indices.
        // E.g. edges [5,6,7,0,1] — low part is [0,1], high part is [5,6,7]
        // We rotate the polygon so that the vertex AFTER the chain's end becomes index 0.
        const lastChainEdge = chainEdgeIndices[chainEdgeIndices.length - 1];
        const afterChainVert = (lastChainEdge + 2) % poly.length;

        if (afterChainVert > 0) {
          // Rotate: move points [0..afterChainVert-1] to the end
          const head = poly.points.splice(0, afterChainVert);
          poly.points.push(...head);

          // Remap and sort chain edge indices
          chainEdgeIndices = chainEdgeIndices.map((idx) => (idx - afterChainVert + poly.length) % poly.length).sort((a, b) => a - b);

          // Also remap the crossing edge indices
          if (lastCrossingStartEdge !== null) {
            lastCrossingStartEdge = (lastCrossingStartEdge - afterChainVert + poly.length) % poly.length;
          }
          if (lastCrossingEndEdge !== null) {
            lastCrossingEndEdge = (lastCrossingEndEdge - afterChainVert + poly.length) % poly.length;
          }
        }
      }
    }

    // --- Trim chain edges at intersection points ---
    // After finding the chain, split crossing edges by inserting vertices at intersection points
    // Process both, accounting for the fact that the first insertion shifts indices for the second

    // Check if the chain is contiguous: each consecutive pair of edges must share a vertex
    // (edge i ends at vertex (i+1) % polyLen, edge i+1 starts at vertex i+1)
    const isChainContiguous = (indices: number[], polyLength: number): boolean => {
      for (let i = 0; i < indices.length - 1; i++) {
        const curEnd = (indices[i] + 1) % polyLength;
        const nextStart = indices[i + 1];
        if (curEnd !== nextStart) return false;
      }
      return true;
    };

    if (trimAtStart && startTrimPoint && lastCrossingStartEdge !== null) {
      const startEdgeIndexInChain = chainEdgeIndices.indexOf(lastCrossingStartEdge);

      if (startEdgeIndexInChain !== -1) {
        // Remove the edge from the chain temporarily
        chainEdgeIndices.splice(startEdgeIndexInChain, 1);

        // Insert a new vertex at the intersection point (creates split edge)
        const insertIdx = lastCrossingStartEdge + 1;
        poly.points.splice(insertIdx, 0, { x: startTrimPoint.x, y: startTrimPoint.y });

        // Update all chain edge indices at or after the insertion
        for (let i = 0; i < chainEdgeIndices.length; i++) {
          if (chainEdgeIndices[i] >= insertIdx) {
            chainEdgeIndices[i]++;
          }
        }

        // Also update the end crossing edge index if affected
        if (lastCrossingEndEdge !== null && lastCrossingEndEdge >= insertIdx) {
          lastCrossingEndEdge++;
        }

        // After split we have two edges:
        // - First half: edge lastCrossingStartEdge (vertex lastCrossingStartEdge → vertex insertIdx)
        // - Second half: edge insertIdx (vertex insertIdx → vertex insertIdx+1)
        // Pick the half that keeps the chain contiguous; try second half first (interior side)
        const halfA = insertIdx;
        const halfB = lastCrossingStartEdge;

        chainEdgeIndices.splice(startEdgeIndexInChain, 0, halfA);
        if (!isChainContiguous(chainEdgeIndices, poly.length)) {
          // Flip to the other half
          chainEdgeIndices[startEdgeIndexInChain] = halfB;
        }

        const edgeToAdd = chainEdgeIndices[startEdgeIndexInChain];

        // Debug visualization - show the edge that's actually in the chain
        if (dbg?.debugStartOuterEdges && linePromptDebug) {
          const edgeStart = poly.points[edgeToAdd];
          const edgeEnd = poly.points[(edgeToAdd + 1) % poly.length];
          linePromptDebug.startOuterEdges.push({
            start: cmToPx(edgeStart),
            end: cmToPx(edgeEnd),
          });
        }
      }
    }

    if (trimAtEnd && endTrimPoint && lastCrossingEndEdge !== null) {
      const endEdgeIndexInChain = chainEdgeIndices.indexOf(lastCrossingEndEdge);

      if (endEdgeIndexInChain !== -1) {
        // Remove the edge from the chain temporarily
        chainEdgeIndices.splice(endEdgeIndexInChain, 1);

        // Insert a new vertex at the intersection point
        const insertIdx = lastCrossingEndEdge + 1;
        poly.points.splice(insertIdx, 0, { x: endTrimPoint.x, y: endTrimPoint.y });

        // Update all chain edge indices at or after the insertion
        for (let i = 0; i < chainEdgeIndices.length; i++) {
          if (chainEdgeIndices[i] >= insertIdx) {
            chainEdgeIndices[i]++;
          }
        }

        // After split we have two edges:
        // - First half: edge lastCrossingEndEdge (vertex lastCrossingEndEdge → vertex insertIdx)
        // - Second half: edge insertIdx (vertex insertIdx → vertex insertIdx+1)
        // Pick the half that keeps the chain contiguous; try first half first (interior side)
        const halfA = lastCrossingEndEdge;
        const halfB = insertIdx;

        chainEdgeIndices.splice(endEdgeIndexInChain, 0, halfA);
        if (!isChainContiguous(chainEdgeIndices, poly.length)) {
          // Flip to the other half
          chainEdgeIndices[endEdgeIndexInChain] = halfB;
        }

        const edgeToAdd = chainEdgeIndices[endEdgeIndexInChain];

        // Debug visualization - show the edge that's actually in the chain
        if (dbg?.debugEndOuterEdges && linePromptDebug) {
          const edgeStart = poly.points[edgeToAdd];
          const edgeEnd = poly.points[(edgeToAdd + 1) % poly.length];
          linePromptDebug.endOuterEdges.push({
            start: cmToPx(edgeStart),
            end: cmToPx(edgeEnd),
          });
        }
      }
    }

    // Debug: record the chain AFTER trimming
    if (linePromptDebug && (dbg?.debugChains || dbg?.debugSelectedChain)) {
      const chainEdges: { start: Point2D; end: Point2D }[] = [];
      for (const eIdx of chainEdgeIndices) {
        const eStart = poly.points[eIdx];
        const eEnd = poly.points[(eIdx + 1) % poly.length];
        chainEdges.push({ start: cmToPx(eStart), end: cmToPx(eEnd) });
      }
      if (dbg?.debugChains) {
        linePromptDebug.allChains.push({ edges: chainEdges, selected: true });
      }
      if (dbg?.debugSelectedChain) {
        linePromptDebug.selectedChainOriginal = chainEdges;
      }
    }

    // --- Determine mode BEFORE collapsing the chain ---
    // Compute the chain span using its first and last+1 vertices.
    const chainFirstVertIdx = chainEdgeIndices[0];
    const chainLastEdgeIdx = chainEdgeIndices[chainEdgeIndices.length - 1];
    // Don't modulo here: if chainLastEdgeIdx + 1 === poly.length, it means the
    // chain's end vertex is vertex 0 (polygon wraps). We track this separately
    // so the pull-out splice logic can handle it correctly.
    const chainLastVertRaw = chainLastEdgeIdx + 1;
    const chainLastVertIdx = chainLastVertRaw % poly.length;
    const chainWrapsToStart = chainLastVertRaw >= poly.length;
    const chainStartPt = poly.points[chainFirstVertIdx];
    const chainEndPt = poly.points[chainLastVertIdx];

    const chainSpanDx = chainEndPt.x - chainStartPt.x;
    const chainSpanDy = chainEndPt.y - chainStartPt.y;
    const chainSpanLength = Math.sqrt(chainSpanDx * chainSpanDx + chainSpanDy * chainSpanDy);

    // Compute prompt projection length onto the chain span direction
    const chainSpanDir: Point2D = chainSpanLength > 1e-6 ? { x: chainSpanDx / chainSpanLength, y: chainSpanDy / chainSpanLength } : direction;
    const promptStartOnSpan = (startCm.x - chainStartPt.x) * chainSpanDir.x + (startCm.y - chainStartPt.y) * chainSpanDir.y;
    const promptEndOnSpan = (endCm.x - chainStartPt.x) * chainSpanDir.x + (endCm.y - chainStartPt.y) * chainSpanDir.y;
    const promptProjLength = Math.abs(promptEndOnSpan - promptStartOnSpan);

    // Refine isFullLoop: only treat as full loop if chain covers all edges AND prompt is substantial
    // This prevents treating localized pullouts on wrapped chains as full loops
    const isActualFullLoop = isFullLoop && promptProjLength > chainSpanLength * 0.5;

    const isPromptSmallerThanChain = promptProjLength < chainSpanLength * pullModeRatio && !isActualFullLoop;

    if (isActualFullLoop) {
      // Chain spans entire polygon: project each vertex onto the prompt line
      for (let i = 0; i < poly.length; i++) {
        const pt = poly.points[i];
        const projOnDir = (pt.x - startCm.x) * direction.x + (pt.y - startCm.y) * direction.y;
        poly.points[i] = {
          x: startCm.x + projOnDir * direction.x,
          y: startCm.y + projOnDir * direction.y,
        };
      }
      continue;
    }

    if (isPromptSmallerThanChain) {
      // --- Pull-out mode: prompt is smaller than the chain ---
      // Preserve the original chain edges outside the pull-out region.
      // Find where perpendicular rays from the prompt line endpoints intersect
      // the chain edges, split at those points, and pull the inner section.

      // Walk along the chain to find cumulative parametric positions for each vertex
      // and identify which edges the prompt boundaries cut through.
      const chainVertices: Point2D[] = [];
      for (const eIdx of chainEdgeIndices) {
        chainVertices.push({ x: poly.points[eIdx].x, y: poly.points[eIdx].y });
      }
      chainVertices.push({ x: poly.points[chainLastVertIdx].x, y: poly.points[chainLastVertIdx].y });

      // For each prompt endpoint, cast a ray perpendicular to the prompt line
      // and find where it crosses a chain edge.
      const findChainCut = (promptPt: Point2D): { edgeLocalIdx: number; point: Point2D } | null => {
        // Cast ray perpendicular to the prompt line and find the CLOSEST intersection
        let bestDist = Infinity;
        let bestResult: { edgeLocalIdx: number; point: Point2D } | null = null;

        for (let ci = 0; ci < chainEdgeIndices.length; ci++) {
          const eStart = chainVertices[ci];
          const eEnd = chainVertices[ci + 1];
          const eDx = eEnd.x - eStart.x;
          const eDy = eEnd.y - eStart.y;
          const eLen = Math.sqrt(eDx * eDx + eDy * eDy);
          if (eLen < 1e-8) continue;

          const eDir: Point2D = { x: eDx, y: eDy };
          const hit = ctx.infiniteLineIntersection(promptPt, normal, eStart, eDir);
          if (!hit) continue;

          const t = ((hit.x - eStart.x) * eDx + (hit.y - eStart.y) * eDy) / (eLen * eLen);
          if (t >= -0.01 && t <= 1.01) {
            const clamped = Math.max(0, Math.min(1, t));
            const cutPt: Point2D = {
              x: eStart.x + clamped * eDx,
              y: eStart.y + clamped * eDy,
            };
            const dist = (cutPt.x - promptPt.x) ** 2 + (cutPt.y - promptPt.y) ** 2;
            if (dist < bestDist) {
              bestDist = dist;
              bestResult = { edgeLocalIdx: ci, point: cutPt };
            }
          }
        }

        if (bestResult) return bestResult;

        // Fallback: find the closest point on any chain edge by projection
        for (let ci = 0; ci < chainEdgeIndices.length; ci++) {
          const eStart = chainVertices[ci];
          const eEnd = chainVertices[ci + 1];
          const eDx = eEnd.x - eStart.x;
          const eDy = eEnd.y - eStart.y;
          const eLen = Math.sqrt(eDx * eDx + eDy * eDy);
          if (eLen < 1e-8) continue;
          const t = Math.max(0, Math.min(1, ((promptPt.x - eStart.x) * eDx + (promptPt.y - eStart.y) * eDy) / (eLen * eLen)));
          const proj: Point2D = { x: eStart.x + t * eDx, y: eStart.y + t * eDy };
          const dist = (proj.x - promptPt.x) ** 2 + (proj.y - promptPt.y) ** 2;
          if (dist < bestDist) {
            bestDist = dist;
            bestResult = { edgeLocalIdx: ci, point: proj };
          }
        }
        return bestResult;
      };

      const cutA = findChainCut(startCm);
      const cutB = findChainCut(endCm);

      if (!cutA || !cutB) continue;

      // Order cuts along the chain so cutStart comes first
      let cutStart = cutA;
      let cutEnd = cutB;
      let movedStart: Point2D = { x: startCm.x, y: startCm.y };
      let movedEnd: Point2D = { x: endCm.x, y: endCm.y };

      const dSq = (a: Point2D, b: Point2D) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
      if (cutA.edgeLocalIdx > cutB.edgeLocalIdx || (cutA.edgeLocalIdx === cutB.edgeLocalIdx && dSq(cutA.point, chainVertices[cutA.edgeLocalIdx]) > dSq(cutB.point, chainVertices[cutB.edgeLocalIdx]))) {
        cutStart = cutB;
        cutEnd = cutA;
        movedStart = { x: endCm.x, y: endCm.y };
        movedEnd = { x: startCm.x, y: startCm.y };
      }

      // Build the new points for this polygon section:
      // [vertices before cutStart edge] -> cutStart.point -> movedStart -> movedEnd -> cutEnd.point -> [vertices after cutEnd edge] -> chain end vertex
      const newChainPoints: Point2D[] = [];
      const tolerance = 0.5;

      // Check if cuts are too close to chain ends to pull out
      // Based on projection distance along the collapsed chain (chainStartPt to chainEndPt)
      // Use (1 - pullModeRatio) / 2 as the threshold ratio relative to chain length
      const skipPulloutThresholdRatio = (1 - pullModeRatio) / 2;

      // Project the prompt line points onto the collapsed chain direction
      const collapsedChainDx = chainEndPt.x - chainStartPt.x;
      const collapsedChainDy = chainEndPt.y - chainStartPt.y;
      const collapsedChainLength = Math.sqrt(collapsedChainDx * collapsedChainDx + collapsedChainDy * collapsedChainDy);

      let skipStartPullout = false;
      let skipEndPullout = false;

      if (collapsedChainLength > 1e-6) {
        const collapsedChainDir: Point2D = {
          x: collapsedChainDx / collapsedChainLength,
          y: collapsedChainDy / collapsedChainLength,
        };

        // Project movedStart and movedEnd onto the collapsed chain
        const movedStartProj = (movedStart.x - chainStartPt.x) * collapsedChainDir.x + (movedStart.y - chainStartPt.y) * collapsedChainDir.y;
        const movedEndProj = (movedEnd.x - chainStartPt.x) * collapsedChainDir.x + (movedEnd.y - chainStartPt.y) * collapsedChainDir.y;

        // Check if projections are within the threshold distance of the chain ends
        // Only skip pullout if the projection is INSIDE the chain but close to the end
        // Negative projections (outside the chain) should NOT skip pullout
        const skipThresholdDistance = collapsedChainLength * skipPulloutThresholdRatio;
        skipStartPullout = movedStartProj >= 0 && movedStartProj < skipThresholdDistance;
        skipEndPullout = movedEndProj <= collapsedChainLength && movedEndProj > collapsedChainLength - skipThresholdDistance;
      }

      // Vertices from chain start up to (but not including) the start of the cut edge
      for (let ci = 0; ci < cutStart.edgeLocalIdx; ci++) {
        newChainPoints.push({ ...chainVertices[ci] });
      }

      // Handle start pullout
      const cutStartEdgeStart = chainVertices[cutStart.edgeLocalIdx];
      if (skipStartPullout) {
        // Project the edge vertex onto the prompt line
        const chainStartEdgeVertex = chainVertices[cutStart.edgeLocalIdx];
        const projOnDir = (chainStartEdgeVertex.x - startCm.x) * direction.x + (chainStartEdgeVertex.y - startCm.y) * direction.y;
        const projectedPoint: Point2D = {
          x: startCm.x + projOnDir * direction.x,
          y: startCm.y + projOnDir * direction.y,
        };
        newChainPoints.push(projectedPoint);
      } else {
        // Normal pullout: add edge vertex, cut point (if different), and pulled point
        newChainPoints.push({ ...chainVertices[cutStart.edgeLocalIdx] });
        if (Math.sqrt(dSq(cutStart.point, cutStartEdgeStart)) > tolerance) {
          newChainPoints.push({ ...cutStart.point });
        }
        newChainPoints.push(movedStart);
      }

      // Handle end pullout
      const cutEndEdgeEnd = chainVertices[cutEnd.edgeLocalIdx + 1];
      if (skipEndPullout) {
        // Project the edge vertex onto the prompt line
        const chainEndEdgeVertex = chainVertices[cutEnd.edgeLocalIdx + 1];
        const projOnDir = (chainEndEdgeVertex.x - startCm.x) * direction.x + (chainEndEdgeVertex.y - startCm.y) * direction.y;
        const projectedPoint: Point2D = {
          x: startCm.x + projOnDir * direction.x,
          y: startCm.y + projOnDir * direction.y,
        };
        newChainPoints.push(projectedPoint);
      } else {
        // Normal pullout: add pulled point and cut point (if different)
        // Don't add the edge vertex here - it's added in the remaining vertices loop
        newChainPoints.push(movedEnd);
        if (Math.sqrt(dSq(cutEnd.point, cutEndEdgeEnd)) > tolerance) {
          newChainPoints.push({ ...cutEnd.point });
        }
      }

      // Vertices from after the cut end edge to chain end
      // When skipEndPullout, we already added the projected vertex, so skip cutEnd.edgeLocalIdx + 1
      const startIdx = skipEndPullout ? cutEnd.edgeLocalIdx + 2 : cutEnd.edgeLocalIdx + 1;
      for (let ci = startIdx; ci < chainVertices.length; ci++) {
        newChainPoints.push({ ...chainVertices[ci] });
      }

      // Replace the chain section in the polygon points array.
      // After wrap-around normalization, chain edges are always contiguous.
      // The chain occupies vertices from chainFirstVertIdx through the end of the
      // chain. If chainWrapsToStart is true, the chain's last vertex is vertex 0
      // (the polygon closing vertex), meaning the chain spans:
      //   chainFirstVertIdx .. poly.length-1 AND vertex 0
      if (chainWrapsToStart) {
        // Chain spans from chainFirstVertIdx to end of array, plus vertex 0.
        // Remove vertex 0 first (shift), then remove from chainFirstVertIdx to end,
        // then insert newChainPoints at chainFirstVertIdx.
        poly.points.splice(0, 1); // remove vertex 0 (chain end vertex)
        // After removing vertex 0, all indices shift down by 1
        const adjustedFirst = chainFirstVertIdx - 1;
        const removeCount = poly.points.length - adjustedFirst;
        poly.points.splice(adjustedFirst, removeCount, ...newChainPoints);
      } else if (chainFirstVertIdx <= chainLastVertIdx) {
        const removeCount = chainLastVertIdx - chainFirstVertIdx + 1;
        poly.points.splice(chainFirstVertIdx, removeCount, ...newChainPoints);
      } else {
        // Should not happen after normalization, but as a safety net
        poly.points.splice(chainFirstVertIdx, poly.points.length - chainFirstVertIdx);
        poly.points.splice(0, chainLastVertIdx + 1);
        poly.points.splice(0, 0, ...newChainPoints);
      }

      // Debug: record collapsed edge (the original chain endpoints before moving)
      if (dbg?.debugCollapsedEdge && linePromptDebug) {
        linePromptDebug.collapsedEdge = {
          start: cmToPx(chainStartPt),
          end: cmToPx(chainEndPt),
        };
      }
    } else {
      // --- Standard mode: prompt line is similar size or bigger ---
      // Collapse chain to a single edge, then slide endpoints onto the prompt line.

      // The chain is a sequence of edge indices. Each edge i connects vertex i to vertex i+1.
      // To collapse the chain, we keep the first vertex and the last+1 vertex, removing all in between.
      const firstEdgeIdx = chainEdgeIndices[0];
      const lastEdgeIdx = chainEdgeIndices[chainEdgeIndices.length - 1];

      // Store the actual point values before we start removing vertices
      const chainStartPt = { x: poly.points[firstEdgeIdx].x, y: poly.points[firstEdgeIdx].y };
      const chainEndPt = {
        x: poly.points[(lastEdgeIdx + 1) % poly.length].x,
        y: poly.points[(lastEdgeIdx + 1) % poly.length].y,
      };

      // Collapse the chain by removing intermediate vertices
      // Keep indices of the collapsed edge before modification
      let edgeStartIdx = firstEdgeIdx;
      let edgeEndIdx = (lastEdgeIdx + 1) % poly.length;

      if (chainEdgeIndices.length > 1) {
        // Determine if we need to handle wrap-around
        // If lastEdgeIdx < firstEdgeIdx, the chain wraps around vertex 0
        const wrapsAround = lastEdgeIdx < firstEdgeIdx;

        if (wrapsAround) {
          // Chain wraps: remove [0..lastEdgeIdx) and (firstEdgeIdx..end]
          // After this, only keep vertex 0 (which is edgeEndIdx) and vertex at adjusted position
          const countFromStart = lastEdgeIdx;
          const countFromEnd = poly.length - firstEdgeIdx - 1;

          // Remove from end first (higher indices)
          if (countFromEnd > 0) {
            poly.points.splice(firstEdgeIdx + 1, countFromEnd);
          }
          // Then remove from start (except the last vertex of the chain)
          if (countFromStart > 0) {
            poly.points.splice(0, countFromStart);
          }

          // After removal, the chain start is at index 0, chain end is at index 1
          edgeStartIdx = 0;
          edgeEndIdx = 1;
        } else {
          // Chain is contiguous: remove all vertices between firstEdgeIdx and edgeEndIdx
          const removeCount = edgeEndIdx - firstEdgeIdx - 1;
          if (removeCount > 0) {
            poly.points.splice(firstEdgeIdx + 1, removeCount);
          }

          // After removal, start is still at firstEdgeIdx, end is now at firstEdgeIdx + 1
          edgeStartIdx = firstEdgeIdx;
          edgeEndIdx = firstEdgeIdx + 1;
        }
      }

      // Project original chain endpoints directly onto the prompt line
      const projStart = (chainStartPt.x - startCm.x) * direction.x + (chainStartPt.y - startCm.y) * direction.y;
      const newStartPt: Point2D = {
        x: startCm.x + projStart * direction.x,
        y: startCm.y + projStart * direction.y,
      };

      const projEnd = (chainEndPt.x - startCm.x) * direction.x + (chainEndPt.y - startCm.y) * direction.y;
      const newEndPt: Point2D = {
        x: startCm.x + projEnd * direction.x,
        y: startCm.y + projEnd * direction.y,
      };

      // Check if neighbor edges are parallel to the prompt line.
      // If parallel (< 45 deg angle), insert a perpendicular connector segment
      // instead of just moving the endpoint (which would distort the neighbor edge).
      const PARALLEL_THRESHOLD = Math.cos(Math.PI / 4); // ~0.7071
      const PERP_DIST_TOLERANCE = 0.5; // cm — skip connector if distance is negligible

      // Start-side neighbor check
      let insertPerpendicularAtStart = false;
      const startNeighborIdx = (edgeStartIdx - 1 + poly.length) % poly.length;
      const startNeighborPt = poly.points[startNeighborIdx];
      const snDx = chainStartPt.x - startNeighborPt.x;
      const snDy = chainStartPt.y - startNeighborPt.y;
      const snLen = Math.sqrt(snDx * snDx + snDy * snDy);
      if (snLen > 1e-6) {
        const startParallelism = Math.abs((snDx / snLen) * direction.x + (snDy / snLen) * direction.y);
        const perpDistStart = Math.sqrt((newStartPt.x - chainStartPt.x) ** 2 + (newStartPt.y - chainStartPt.y) ** 2);
        insertPerpendicularAtStart = startParallelism > PARALLEL_THRESHOLD && perpDistStart > PERP_DIST_TOLERANCE;
      }

      // End-side neighbor check
      let insertPerpendicularAtEnd = false;
      const endNeighborIdx = (edgeEndIdx + 1) % poly.length;
      const endNeighborPt = poly.points[endNeighborIdx];
      const enDx = endNeighborPt.x - chainEndPt.x;
      const enDy = endNeighborPt.y - chainEndPt.y;
      const enLen = Math.sqrt(enDx * enDx + enDy * enDy);
      if (enLen > 1e-6) {
        const endParallelism = Math.abs((enDx / enLen) * direction.x + (enDy / enLen) * direction.y);
        const perpDistEnd = Math.sqrt((newEndPt.x - chainEndPt.x) ** 2 + (newEndPt.y - chainEndPt.y) ** 2);
        insertPerpendicularAtEnd = endParallelism > PARALLEL_THRESHOLD && perpDistEnd > PERP_DIST_TOLERANCE;
      }

      // Build replacement vertices for the collapsed chain section.
      // The 2 vertices at edgeStartIdx and edgeEndIdx are replaced with 2-4 vertices
      // depending on whether perpendicular connectors are needed.
      const replacementPoints: Point2D[] = [];

      if (insertPerpendicularAtStart) {
        // Keep original chain start (neighbor edge unchanged), then add projected point
        replacementPoints.push({ x: chainStartPt.x, y: chainStartPt.y });
        replacementPoints.push(newStartPt);
      } else {
        replacementPoints.push(newStartPt);
      }

      if (insertPerpendicularAtEnd) {
        // Add projected end point, then original chain end (neighbor edge unchanged)
        replacementPoints.push(newEndPt);
        replacementPoints.push({ x: chainEndPt.x, y: chainEndPt.y });
      } else {
        replacementPoints.push(newEndPt);
      }

      poly.points.splice(edgeStartIdx, 2, ...replacementPoints);

      // Debug: record collapsed edge (the original chain endpoints before projection)
      if (dbg?.debugCollapsedEdge && linePromptDebug) {
        linePromptDebug.collapsedEdge = {
          start: cmToPx(chainStartPt),
          end: cmToPx(chainEndPt),
        };
      }
    }
  }

  return { polygons: adjustedPolygons, linePromptDebug };
}
